import React, {useLayoutEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS, FlexRow, Heading, Text, WorkSignifier} from '@forecast-it/design-system';

/**
 * The designer's own screen, built from design-system primitives because the design system ships
 * no Takeover. It is deliberately NOT a Modal: the designer opens several dialogs of its own
 * through the modal conductor, and nesting those inside a dialog makes Escape close the whole
 * screen instead of the innermost layer.
 *
 * It fills the route area rather than covering the app: the project navigation stays reachable,
 * collapsed to its rail while the designer is open (see the page's own collapse effect). A fixed,
 * viewport-covering surface hid that navigation entirely and left no way out but the header.
 *
 * The actions sit in a footer, in the design's order — create, cancel, download — with a close
 * button in the header that cancels too, so the way out is reachable from either end of a long
 * settings column.
 */
const Surface = styled.div<{$offsetTop: number}>`
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	background: ${({theme}) => theme.sys.color.background.neutral['1']};
	/*
	 * A definite height, or nothing in the chain has one: the route row the app shell puts this in
	 * is a flex child with the default min-height:auto, so without this the row grows to fit the
	 * settings column, the whole page scrolls, and the footer ends up thousands of pixels down.
	 *
	 * The offset is measured rather than hardcoded — it is whatever chrome the shell renders above
	 * this surface, and a constant here would drift silently the day that changes.
	 */
	height: ${p => `calc(100vh - ${p.$offsetTop}px)`};
	overflow: hidden;
`;
const Bar = styled.header`
	flex: none;
	padding: 12px 20px;
	border-bottom: 1px solid ${({theme}) => theme.sys.color.border.decorative};
`;
const Body = styled.div`
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
`;
const Footer = styled.footer`
	flex: none;
	padding: 12px 20px;
	border-top: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	background: ${({theme}) => theme.sys.color.background.neutral['1']};
`;
const TitleGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

type Props = {
	title: string;
	projectName: string;
	projectIndicator: string;
	projectColor?: string | null;
	closeLabel: string;
	cancelLabel: string;
	onCancel: () => void;
	downloadLabel: string;
	onDownload: () => void;
	primaryLabel: string;
	onPrimary: () => void;
	primaryDisabled?: boolean;
	children: React.ReactNode;
};

const QuoteDesignerTakeover: React.FC<Props> = ({
	title,
	projectName,
	projectIndicator,
	projectColor,
	closeLabel,
	cancelLabel,
	onCancel,
	downloadLabel,
	onDownload,
	primaryLabel,
	onPrimary,
	primaryDisabled,
	children,
}) => {
	// Setting the height does not move the top edge — the chrome above is what fixes it — so one
	// measurement per layout is stable rather than circular.
	const surfaceRef = useRef<HTMLDivElement>(null);
	const [offsetTop, setOffsetTop] = useState(0);
	useLayoutEffect(() => {
		const measure = () => {
			const element = surfaceRef.current;
			if (element) setOffsetTop(Math.round(element.getBoundingClientRect().top + window.scrollY));
		};
		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, []);

	return (
		<Surface
			ref={surfaceRef}
			$offsetTop={offsetTop}
			data-testid="quote-designer-takeover"
			data-cy="quote-designer-takeover"
			data-userpilot="quote-designer"
		>
			<Bar>
				<FlexRow alignItems="center" justifyContent="space-between" gap="l">
					<TitleGroup>
						{/* The work item this belongs to: its id beside its name, above the screen's own title. */}
						<FlexRow gap="s" alignItems="center">
							<WorkSignifier workId={projectIndicator} name={projectName} color={projectColor || ''} />
							<Text color="auxiliary">{projectName}</Text>
						</FlexRow>
						{/* The document's real h1 — `size` is typography only and carries no heading level. */}
						<Heading size="3" as="h1" data-cy="quote-designer-title" data-userpilot="quote-designer-title">
							{title}
						</Heading>
					</TitleGroup>
					<Button
						emphasis={EMPHASIS.MONO}
						icon="close"
						iconOnly
						onPress={onCancel}
						aria-label={closeLabel}
						data-cy="quote-designer-close"
					/>
				</FlexRow>
			</Bar>
			<Body>{children}</Body>
			<Footer>
				<FlexRow gap="s" alignItems="center">
					<Button onPress={onPrimary} disabled={primaryDisabled} data-cy="publish-quote-button">
						{primaryLabel}
					</Button>
					<Button emphasis={EMPHASIS.MEDIUM} onPress={onCancel} data-cy="quote-designer-cancel">
						{cancelLabel}
					</Button>
					{/* Outlined, like Cancel: the design reads this as a button, not a bare label. */}
					<Button emphasis={EMPHASIS.MEDIUM} icon="download" onPress={onDownload} data-cy="download-quote-pdf-button">
						{downloadLabel}
					</Button>
				</FlexRow>
			</Footer>
		</Surface>
	);
};

export default QuoteDesignerTakeover;
